import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { DUAL_PANEL_SELECTABLE_TYPES, PER_PAGE } from '../../../../../../constants/dual-panel-selector-constants'
import DualPanelSelector from '../../../../../common/dual-panel-selector/DualPanelSelector'
import { selectProgramsData, setSearchValue } from '../../../../../../reducers/dualPanelSelectorReducer'
import classnames from 'classnames'
import Modal from '../../../../../common/Modal'
import SubmitButton from '../../../../../common/buttons/SubmitButton'

import classes from './WaitlistProgramSelectorModal.module.scss'
import { ReactComponent as PersonIcon } from '../../../../../../assets/images/common/icons/person-icon.svg'
import { getProgramsDualPanelSelectorThunk } from '../../../../../../actions/programActions'
import { categoryCompare } from '../../../../../../utility/programHelper'

const SummaryPlaceholder = () => {
  const { t } = useTranslation()

  return (
    <div className={classes.placeholder}>
      <PersonIcon className={classes.icon} />
      <p className={classes.title}>{t('programs.manage.waitlistProgramSelector.noNewStudentsAdded')}</p>
      <p className={classes.description}>
        {t('programs.manage.waitlistProgramSelector.allStudentsDisabled')}
      </p>
    </div>
  )
}

const WaitlistProgramSelectorModal = ({
  title,
  onProceed,
  currentProgram,
  selectedProgram,
  students,
  onSelectProgram,
  includeCurrentProgram = false,
  ...props
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const programsData = useSelector(selectProgramsData)

  const fetchPrograms = useCallback((page, searchTerm, completed) =>
    dispatch(getProgramsDualPanelSelectorThunk({ page, perPage: PER_PAGE, searchTerm, completed }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [dispatch, t])

  const leftPanelProps = {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.programs],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.programs]: {
        titleText: t('programs.manage.waitlistProgramSelector.selectProgramTitle'),
        allItems: includeCurrentProgram ? programsData.items : programsData.items.filter(program => program.id !== currentProgram.id),
        withCompletedSwitcher: false,
        searchValue: programsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.programs })),
        getSelectedItem: (id) => selectedProgram?.id === id ? selectedProgram : undefined,
        fetchItems: fetchPrograms,
        pagination: programsData.pagination,
        onCheckChange: (checked, program) => onSelectProgram(checked ? null : program),
        itemCategoryTitle: (program) => program.category ? program.category.name : t('programs.common.otherPrograms'),
        itemCategoryCompare: categoryCompare,
        isLoading: programsData.isLoading,
      },
    },
  }

  const rightPanelProps = {
    selectedItems: students,
    selectedItemType: DUAL_PANEL_SELECTABLE_TYPES.students,
    summaryPlaceholder: <SummaryPlaceholder />,
  }

  return (
    <Modal className={classes.modal} title={title} {...props}>
      <div className={classnames('d-flex flex-column', classes.modalContentWrapper)}>
        <DualPanelSelector
          type='WaitlistProgramRoster'
          className={classes.dualPanelSelector}
          {...leftPanelProps}
          {...rightPanelProps}
        />
        <div className='mt-3 d-flex justify-content-end'>
          <SubmitButton
            disabled={!selectedProgram}
            className={classes.button}
            buttonMessage={t('common.action.continue')}
            onClick={onProceed}
          />
        </div>
      </div>
    </Modal>
  )
}

WaitlistProgramSelectorModal.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool,
  currentProgram: PropTypes.object.isRequired,
  selectedProgram: PropTypes.object,
  students: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  onSelectProgram: PropTypes.func.isRequired,
  includeCurrentProgram: PropTypes.bool,
}

export default WaitlistProgramSelectorModal
